import { FormControl, NonNullableFormBuilder } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

import { COST_CENTER_PATTERN } from '@shared-constants/pattern-constants';
import { DEBOUNCE_TIMEOUT } from '@shared-constants/ui-constants';
import { IHttpResponseCostCenter } from '@shared-interfaces/api';

import { ApiService } from '../services/api.service';
import { UserService } from '../services/user.service';

export class CostCenterHandler {
  ctrl = this.fb.control('');
  finishedLookupSubject$ = new BehaviorSubject(false);

  description: string;
  private currentRequest = new Subscription();
  private isInitialized: boolean;

  public shouldMissingCostCenterThrowError = true;

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly userService: UserService,
    private readonly apiService: ApiService,
  ) {
    this.description = '';
    this.isInitialized = false;
  }

  getCostCenterValue(): string {
    if (!this.ctrl.value && !this.isInitialized) this.initialize();
    return this.ctrl.value.trim();
  }

  getCostCenterFormControl(): FormControl<string> {
    if (!this.ctrl.value) this.initialize();
    return this.ctrl;
  }

  setCostCenterValue(ccObject: IHttpResponseCostCenter, shouldEmit: boolean, shouldSkipValueOverwrite?: boolean): void {
    if (!shouldSkipValueOverwrite) this.ctrl.setValue(ccObject.CostCtr.trim(), { emitEvent: shouldEmit });
    this.description = ccObject.Description;
    this.finishedLookupSubject$.next(true);
  }

  setCostCenterValueWithString(costCenterNumber: string, shouldEmit: boolean): void {
    this.ctrl.setValue(costCenterNumber.trim(), { emitEvent: shouldEmit });
    this.finishedLookupSubject$.next(true);
  }

  private initialize(): void {
    const ccObject = this.userService.getCcInfo();
    this.handleCostCenterChange();
    this.ctrl.setValue(ccObject.CostCenter); //intentionally trigger the value change detection because the user service does not provide the cost center description anymore.
    this.isInitialized = true;
  }

  private handleCostCenterChange(): void {
    this.ctrl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMEOUT))
      .pipe(distinctUntilChanged())
      .pipe(
        map((value: string) => {
          const normalizedValue = value.trim();
          this.ctrl.setErrors(null);

          this.ctrl.updateValueAndValidity();
          if (!normalizedValue && !this.shouldMissingCostCenterThrowError) return;
          if (!normalizedValue) {
            this.ctrl.markAsTouched();
            this.ctrl.setErrors({ noCostCenter: true });
            this.description = '';
            this.finishedLookupSubject$.next(true);
            return;
          }
          if (COST_CENTER_PATTERN.test(normalizedValue)) {
            //Must be exactly 6 digits long
            this.searchSap(normalizedValue);
          } else {
            this.ctrl.markAsTouched();
            this.ctrl.setErrors({ invalid: true });
            this.description = '';
            this.finishedLookupSubject$.next(true);
          }

          // let result = this.datahub.staticData.costCenters.value.find(x => trimLeadingZeros(x.CostCtr) == value)
          // if(!result) return this.searchSap(normalizedValue)
          // this.setCostCenterValue(result, false)
        }),
      )
      .subscribe();
  }

  private searchSap(value: string): void {
    this.currentRequest.unsubscribe(); //Cancel the oldest request. Probably could easily be done with RXJS, but its too hard for me.
    this.currentRequest = this.apiService.getCostCenters$({ CostCtr: value }).subscribe((costCenter) => {
      if (costCenter.length !== 1 || costCenter[0] == null) {
        this.ctrl.markAsTouched();
        this.ctrl.setErrors({ noCostCenter: true });
        this.description = '';
        this.finishedLookupSubject$.next(true);
        return;
      }
      this.setCostCenterValue(costCenter[0], false, true);
    });
  }
}
